import Account, { ACCOUNT_ROLE } from 'src/models/accounting/Account'
import Model, { encoders, ID, Integer, JsonObject, MODEL_LEVEL } from 'src/models/model'

export const SOURCE_TYPES = {
  ORDER_ITEM: 'order_item',
  PAYMENT: 'payment',
  DAILY_DEPOSIT: 'daily_deposit'
}

export const EFFECTIVE_TYPES = {
  SALES: 'sales',
  CASH_BASIS: 'cash-basis',
  ACCRUAL: 'accrual',
  ATTENDANCE: 'attendance',

  CURRENT_SALES: 'current-sales',
  MATURING_SALES: 'maturing-sales',
  FUTURE_SALES: 'future-sales',
  PAST_SALES: 'past-sales',
  RETROACTIVE_SALES: 'retroactive-sales',

  CURRENT_PAYMENTS: 'current-payments',

  UNPAID_PLACEMENTS: 'unpaid-placement',
  PAID_PAST_PLACEMENTS: 'pay-past-place',
  ACCOUNTS_RECEIVABLE: 'accounts-receivable'
}

export const VALUE_OPTIONS = {
  [SOURCE_TYPES.ORDER_ITEM]: {
    RETAIL_PRICE: 'retail-price',
    NET_PAID: 'net-paid',
    GROSS_PAID: 'gross-paid',
    TAX_PAID: 'tax-paid',

    DISCOUNT_AMOUNT: 'discount-amount',

    REDEEMED_VOUCHER_VALUE_NET: 'redeemed-voucher-net',
    REDEEMED_VOUCHER_VALUE_TAX: 'redeemed-voucher-tax',
    REDEEMED_VOUCHER_VALUE_GROSS: 'redeemed-voucher-gross',

    RECOGNIZED_VOUCHER_NET: 'recognized-voucher-net',
    RECOGNIZED_VOUCHER_TAX: 'recognized-voucher-tax',
    RECOGNIZED_VOUCHER_GROSS: 'recognized-voucher-gross',

    POINTS_EARNED: 'points-earned',
    POINTS_REDEEMED: 'points-redeemed'
  },
  [SOURCE_TYPES.PAYMENT]: {
    AMOUNT: 'amount',
    INVERSE_AMOUNT: 'inverse-amount'
  },
  [SOURCE_TYPES.DAILY_DEPOSIT]: {
    CREDIT_CARD_PAYMENT: 'credit-card-payment',
    CREDIT_CARD_REFUND: 'credit-card-refund',
    CREDIT_CARD_NET: 'credit-card-net',
    CREDIT_CARD_NET_MINUS_FEES: 'credit-card-net-minus-fees',

    WINDCAVE_PAYMENT: 'windcave-payment',
    WINDCAVE_REFUND: 'windcave-refund',
    WINDCAVE_NET: 'windcave-net',

    INDY_FEE: 'indy-fee',
    STRIPE_FEE: 'stripe-fee',

    LOST_DISPUTE: 'lost-dispute',
    LOST_DISPUTE_FEE: 'lost-dispute-fee',
    LOST_DISPUTE_AND_FEE: 'lost-dispute-and-fee',

    EXTERNAL_TICKETING_DEPOSIT: 'external-ticketing-deposit',
    DEPOSIT: 'deposit',

    CASH_PAYMENT: 'cash-payment',
    CASH_REFUND: 'cash-refund',
    CASH_DEPOSIT: 'cash-deposit',
    CASH_COUNT_AMOUNT: 'cash-count-amount',
    CASH_OVER_UNDER: 'cash-over-under',
    EXPECTED_CASH_DEPOSIT: 'expected-cash-deposit',
    CASH_COUNT_CASH_NET_OVER_UNDER: 'cash-count-cash-net-over-under',
    CASH_DEPOSIT_OVER_UNDER: 'cash-deposit-over-under'

  }
}

export const VOID_MODE = {
  COMBINED: 'combined',
  PLACE: 'place',
  VOID: 'void'
}

export const REFUND_MODE = {
  COMBINED: 'combined',
  PAYMENT: 'payment',
  REFUND: 'refund'
}

export default class AccountSource extends Model {
  static name = 'AccountSource'

  static deletable = true
  static duplicatable = true
  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Financial]

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true
      },
      account: {
        type: Account,
        encode: encoders.hasOne,
        reference: true
      },
      name: {
        type: String,
        required: true
      },
      reportCode: {
        type: String,
        default: ''
      },
      sourceOptions: {
        type: JsonObject,
        mergeDefault: true,
        default () {
          return {
            type: 'order_item',

            // General Options
            effective_type: 'current-sales',
            group_by_tax: true,
            group_by_class: false,

            value_type: 'net-paid',
            is_contra: false,

            // Order Item Option
            order_item_type_filter: ['Item'],
            exclude_order_item_type: false,
            group_by_order_item_type: true,
            group_by_item_class: false,

            title_class_filter: null,
            exclude_title_class: false,

            item_class_filter: null,
            exclude_item_class: false,

            item_filter: null,
            exclude_item: false,

            activity_filter: null,
            exclude_activity: false,

            // Payment Option
            refund_mode: 'combined',
            group_by_type: true,
            group_by_subtype: false,
            type_filter: ['Card'],
            exclude_type: false,
            subtype_filter: null,
            exclude_subtype: false
          }
        }
      },

      displayOrder: {
        type: Integer,
        default: 100
      }
    }

    this.computed = {
      valueOptions () {
        return VALUE_OPTIONS[this.sourceOptions?.type]
      },
      correspondingAccountSources () {
        const correspondingAccountSources = []

        if (_.isNil(this.account)) return correspondingAccountSources

        if (this.sourceOptions.type === SOURCE_TYPES.ORDER_ITEM) {
          if (this.sourceOptions.value_type === VALUE_OPTIONS[SOURCE_TYPES.ORDER_ITEM].NET_PAID) {
            correspondingAccountSources.push({
              description: `${this.account.name} - Tax Paid - Cash Basis`,
              name: `${this.account.name} - Tax Paid - Cash Basis`,
              value_type: VALUE_OPTIONS[SOURCE_TYPES.ORDER_ITEM].TAX_PAID,
              account_role: ACCOUNT_ROLE.TAX
            })
          }
        }

        if (this.sourceOptions.effective_type === EFFECTIVE_TYPES.CURRENT_SALES) {
          correspondingAccountSources.push({
            description: `${this.account.name} - Maturing Sales - This Account`,
            name: `${this.account.name} - Maturing Sales`,
            value_type: this.sourceOptions.value_type,
            effective_type: EFFECTIVE_TYPES.MATURING_SALES
          })
          correspondingAccountSources.push({
            description: `${this.account.name} - Future Sales - Unearned Revenue`,
            name: `${this.account.name} - Future Sales`,
            value_type: this.sourceOptions.value_type,
            account_role: ACCOUNT_ROLE.UNEARNED_REVENUE,
            effective_type: EFFECTIVE_TYPES.FUTURE_SALES
          })
          correspondingAccountSources.push({
            description: `${this.account.name} - Maturing Sales - Unearned Revenue (Contra)`,
            name: `${this.account.name} - Maturing Sales`,
            value_type: this.sourceOptions.value_type,
            effective_type: EFFECTIVE_TYPES.MATURING_SALES,
            contra: true
          })
          correspondingAccountSources.push({
            description: `${this.account.name} - Retroactive Sales - This Account`,
            name: `${this.account.name} - Retroactive Sales`,
            value_type: this.sourceOptions.value_type,
            effective_type: EFFECTIVE_TYPES.PAST_SALES,
            account_role: ACCOUNT_ROLE.UNEARNED_REVENUE
          })

          correspondingAccountSources.push({
            description: `${this.account.name} - Unpaid Placements - Accounts Receivable`,
            name: `${this.account.name} - Unpaid Placements`,
            effective_type: EFFECTIVE_TYPES.UNPAID_PLACEMENTS,
            account_role: ACCOUNT_ROLE.ACCOUNTS_RECEIVABLE
          })
          correspondingAccountSources.push({
            description: `${this.account.name} - Paid Past Placements - Accounts Receivable (Contra)`,
            name: `${this.account.name} - Paid Past Placements`,
            account_role: ACCOUNT_ROLE.ACCOUNTS_RECEIVABLE,
            effective_type: EFFECTIVE_TYPES.PAY_PAST_PLACEMENT,
            contra: true
          })
        }

        return correspondingAccountSources
      }
    }

    this.watchers = [
      [
        function () { return this.sourceOptions?.type },
        function () {
          this.validateValueType()
          this.validateEffectiveType()
        }
      ]
    ]
  }

  validateValueType () {
    if (!this.sourceOptions) return

    if (!this.valueOptions) return

    // Check for already valid value
    if (_(this.valueOptions).values().includes(this.sourceOptions.value_type)) return

    // Set default for type
    if (this.sourceOptions.type === SOURCE_TYPES.ORDER_ITEM) {
      // Return a specific value for order items
      this.sourceOptions.value_type = VALUE_OPTIONS[SOURCE_TYPES.ORDER_ITEM].GROSS_PAID
    } else {
      // Otherwise return first
      this.sourceOptions.value_type = _.values(this.valueOptions)[0]
    }
  }

  validateEffectiveType () {
    if (!this.sourceOptions) return

    if (this.sourceOptions.type !== SOURCE_TYPES.ORDER_ITEM) {
      this.sourceOptions.effective_type = EFFECTIVE_TYPES.CURRENT
    }
  }
}
